import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Snackbar, SnackbarContent } from '@material-ui/core';
import { hooks, signaling } from '@lifesize/clients.sdk';
import { openInCallDrawer } from 'actions/InCall/InCallDrawer';
import { useDispatch } from 'react-redux';
import { PARTICIPANTS_DRAWER } from 'constants/InCall/InCallDrawer';
import { colors } from '@lifesize/clients.mui-components';
import usePrevious from 'hooks/usePrevious';

const WaitingRoomSnackbar = () => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const waitingRoomUsers = hooks.useWaitingRoomUsers();
  const [open, setOpen] = useState(false);
  const [isMultipleParticipants, setIsMultipleParticipants] = useState(false);
  const [latestParticipant, setWaitingRoomParticipant] = useState({} as any);
  const previousWaitingRoomUsers = usePrevious(waitingRoomUsers, []);

  useEffect(() => {
    if (previousWaitingRoomUsers?.length < waitingRoomUsers?.length) {
      // If there are multiple waiting room users at the same time.
      if (waitingRoomUsers?.length - previousWaitingRoomUsers?.length > 1) {
        setIsMultipleParticipants(true);
        setWaitingRoomParticipant({});
      } else {
        setWaitingRoomParticipant(Array.from(waitingRoomUsers.values()).pop());
        setIsMultipleParticipants(false);
      }
      setOpen(true);
    } else {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingRoomUsers]);

  const allowWaitingRoomParticipant = (participant) => {
    signaling.acceptWaitingRoomParticipant(participant?.participantId);
    setOpen(false);
    setWaitingRoomParticipant({});
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      open={open}
      autoHideDuration={10000}
      onClose={() => {
        setOpen(false);
        setWaitingRoomParticipant({});
      }}
      className={classes.snackbar}
    >
      <SnackbarContent
        className={classes.container}
        data-testid="waitingRoomUser"
        message={
          <Box>
            {isMultipleParticipants ? (
              <FormattedMessage
                id="components.inCall.waitingRoom.snackbar.multiple"
                defaultMessage="Multiple participants entered the Waiting Room"
                values={{ name: latestParticipant?.name }}
              />
            ) : (
              <FormattedMessage
                id="components.inCall.waitingRoom.snackbar"
                defaultMessage="{name} entered the Waiting Room"
                values={{ name: latestParticipant?.name }}
              />
            )}
            <a
              data-testid="open-participants-drawer-snackbar"
              className={classes.action}
              onClick={() => {
                dispatch(openInCallDrawer(PARTICIPANTS_DRAWER));
                setOpen(false);
              }}
            >
              <FormattedMessage id="components.inCall.waitingRoom.view" defaultMessage="view waiting room" />
            </a>
            {!isMultipleParticipants && (
              <a
                data-testid="allow-waiting-room-participant"
                className={classes.action}
                onClick={() => {
                  allowWaitingRoomParticipant(latestParticipant);
                }}
              >
                <FormattedMessage id="components.inCall.waitingRoom.allow.in" defaultMessage="allow in" />
              </a>
            )}
          </Box>
        }
      />
    </Snackbar>
  );
};

const useStyles = makeStyles(() => ({
  snackbar: {
    bottom: 84
  },
  container: {
    padding: '10px 20px',
    borderRadius: 30,
    backgroundColor: colors.white,
    fontSize: 12,
    color: colors.charcoal
  },
  action: {
    color: colors.lifesize,
    cursor: 'pointer',
    marginLeft: 20
  }
}));

export default WaitingRoomSnackbar;
